package fr.manyverse.app.presentation.components.gm_screen

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.MenuBook
import androidx.compose.material.icons.filled.Favorite
import androidx.compose.material.icons.filled.Shield
import androidx.compose.material.icons.filled.Whatshot
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.OutlinedCard
import androidx.compose.material3.Text
import androidx.compose.material3.rememberModalBottomSheetState
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.PathEffect
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import fr.manyverse.app.domain.model.CharacterSheet
import fr.manyverse.app.domain.model.CharacteristicEntry
import fr.manyverse.app.domain.model.GaugeEntry
import fr.manyverse.app.presentation.components.UserErrorSnackbarHost
import fr.manyverse.app.presentation.components.character_sheet.ExpandableDescription
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.character_sheet_characteristics_section
import manyverse.shared.generated.resources.character_sheet_empty
import manyverse.shared.generated.resources.character_sheet_gauges_section
import manyverse.shared.generated.resources.character_sheet_powers_section
import manyverse.shared.generated.resources.character_sheet_skills_section
import org.jetbrains.compose.resources.stringResource

/**
 * Fiche de caractéristiques d'un personnage présentée au MJ.
 *
 * Reprend les visuels du mode lecture de `CharacterSheetTab` (carte de section,
 * barres de jauges, filet de points des caractéristiques, liste des compétences et des pouvoirs).
 * Les **jauges sont éditables** via les boutons +/- (comme sur la fiche du joueur) ;
 * les caractéristiques, compétences et pouvoirs restent en lecture seule.
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun GmPlayerSheetSheet(
    characterName: String,
    sheet: CharacterSheet,
    onIncrement: (gaugeId: String) -> Unit,
    onDecrement: (gaugeId: String) -> Unit,
    onDismiss: () -> Unit
) {
    val sheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true)

    ModalBottomSheet(
        onDismissRequest = onDismiss,
        sheetState = sheetState
    ) {
        // La feuille masque le snackbar de la racine : un échec d'écriture de jauge s'affiche ici
        Box {
            GmPlayerSheetContent(
                characterName = characterName,
                sheet = sheet,
                onIncrement = onIncrement,
                onDecrement = onDecrement
            )
            UserErrorSnackbarHost(modifier = Modifier.align(Alignment.BottomCenter))
        }
    }
}

@Composable
private fun GmPlayerSheetContent(
    characterName: String,
    sheet: CharacterSheet,
    onIncrement: (gaugeId: String) -> Unit,
    onDecrement: (gaugeId: String) -> Unit
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .fillMaxHeight(0.6f)
            .padding(horizontal = 24.dp)
            .padding(bottom = 24.dp)
            .verticalScroll(rememberScrollState()),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        Text(
            text = characterName,
            style = MaterialTheme.typography.titleLarge,
            fontWeight = FontWeight.Bold
        )

        val isEmpty = sheet.gauges.isEmpty() &&
            sheet.characteristics.isEmpty() &&
            sheet.skills.isEmpty() &&
            sheet.powers.isEmpty()

        if (isEmpty) {
            Text(
                text = stringResource(Res.string.character_sheet_empty),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
            return@Column
        }

        if (sheet.gauges.isNotEmpty()) {
            SheetSection(
                icon = Icons.Default.Favorite,
                title = stringResource(Res.string.character_sheet_gauges_section)
            ) {
                sheet.gauges.forEach { gauge ->
                    GaugeRow(
                        gauge = gauge,
                        onIncrement = { onIncrement(gauge.id) },
                        onDecrement = { onDecrement(gauge.id) }
                    )
                }
            }
        }

        if (sheet.characteristics.isNotEmpty()) {
            SheetSection(
                icon = Icons.Default.Shield,
                title = stringResource(Res.string.character_sheet_characteristics_section)
            ) {
                @OptIn(ExperimentalLayoutApi::class)
                FlowRow(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(24.dp),
                    maxItemsInEachRow = 2
                ) {
                    sheet.characteristics.forEach { characteristic ->
                        CharacteristicCell(
                            characteristic = characteristic,
                            modifier = Modifier.weight(1f)
                        )
                    }
                }
            }
        }

        if (sheet.skills.isNotEmpty()) {
            SheetSection(
                icon = Icons.AutoMirrored.Filled.MenuBook,
                title = stringResource(Res.string.character_sheet_skills_section)
            ) {
                sheet.skills.forEachIndexed { index, skill ->
                    Column(modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp)) {
                        Text(
                            text = skill.label,
                            style = MaterialTheme.typography.bodyLarge,
                            modifier = Modifier.fillMaxWidth()
                        )
                        ExpandableDescription(skill.description)
                    }
                    if (index < sheet.skills.lastIndex) {
                        HorizontalDivider(
                            color = MaterialTheme.colorScheme.outline.copy(alpha = 0.2f)
                        )
                    }
                }
            }
        }

        if (sheet.powers.isNotEmpty()) {
            SheetSection(
                icon = Icons.Default.Whatshot,
                title = stringResource(Res.string.character_sheet_powers_section)
            ) {
                sheet.powers.forEachIndexed { index, power ->
                    Column(modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp)) {
                        Text(
                            text = power.label,
                            style = MaterialTheme.typography.bodyLarge,
                            modifier = Modifier.fillMaxWidth()
                        )
                        ExpandableDescription(power.description)
                    }
                    if (index < sheet.powers.lastIndex) {
                        HorizontalDivider(
                            color = MaterialTheme.colorScheme.outline.copy(alpha = 0.2f)
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun SheetSection(
    icon: ImageVector,
    title: String,
    content: @Composable ColumnScope.() -> Unit
) {
    OutlinedCard(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(12.dp)
    ) {
        Column(
            modifier = Modifier.padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(10.dp)
            ) {
                Icon(
                    imageVector = icon,
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.primary,
                    modifier = Modifier.size(20.dp)
                )
                Text(
                    text = title.uppercase(),
                    style = MaterialTheme.typography.labelLarge,
                    fontWeight = FontWeight.SemiBold,
                    letterSpacing = 1.5.sp,
                    color = MaterialTheme.colorScheme.primary
                )
                Box(
                    modifier = Modifier
                        .weight(1f)
                        .height(1.dp)
                        .background(
                            Brush.horizontalGradient(
                                listOf(
                                    MaterialTheme.colorScheme.primary.copy(alpha = 0.3f),
                                    Color.Transparent
                                )
                            )
                        )
                )
            }
            content()
        }
    }
}

@Composable
private fun GaugeRow(
    gauge: GaugeEntry,
    onIncrement: () -> Unit,
    onDecrement: () -> Unit
) {
    val span = gauge.max - gauge.min
    val fraction = if (span > 0) {
        ((gauge.current - gauge.min).toFloat() / span).coerceIn(0f, 1f)
    } else {
        0f
    }
    val gaugeColor = gauge.colorArgb?.let { Color(it) } ?: MaterialTheme.colorScheme.primary

    Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            Text(
                text = gauge.label,
                style = MaterialTheme.typography.bodyLarge,
                modifier = Modifier.weight(1f)
            )
            Row(verticalAlignment = Alignment.Bottom) {
                Text(
                    text = gauge.current.toString(),
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.onSurface
                )
                Text(
                    text = " / ${gauge.max}",
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        }
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            IconButton(
                onClick = onDecrement,
                enabled = gauge.current > gauge.min,
                modifier = Modifier.size(32.dp)
            ) {
                Text("−", style = MaterialTheme.typography.titleLarge)
            }
            LinearProgressIndicator(
                progress = { fraction },
                modifier = Modifier
                    .weight(1f)
                    .height(8.dp)
                    .clip(RoundedCornerShape(4.dp)),
                color = gaugeColor,
                trackColor = MaterialTheme.colorScheme.surfaceVariant
            )
            IconButton(
                onClick = onIncrement,
                enabled = gauge.current < gauge.max,
                modifier = Modifier.size(32.dp)
            ) {
                Text("+", style = MaterialTheme.typography.titleLarge)
            }
        }
    }
}

@Composable
private fun CharacteristicCell(
    characteristic: CharacteristicEntry,
    modifier: Modifier = Modifier
) {
    Column(modifier = modifier.padding(vertical = 6.dp)) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            Text(
                text = characteristic.label,
                style = MaterialTheme.typography.bodyLarge,
                color = MaterialTheme.colorScheme.onSurface
            )
            DottedLeader(modifier = Modifier.weight(1f))
            Text(
                text = characteristic.value.toString(),
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.SemiBold,
                color = MaterialTheme.colorScheme.primary
            )
        }
        ExpandableDescription(characteristic.description)
    }
}

/** Filet de points reliant le libellé à la valeur, comme sur le design. */
@Composable
private fun DottedLeader(modifier: Modifier = Modifier) {
    val color = MaterialTheme.colorScheme.outline.copy(alpha = 0.5f)
    Box(
        modifier = modifier
            .height(1.dp)
            .drawBehind {
                val y = size.height / 2f
                drawLine(
                    color = color,
                    start = Offset(0f, y),
                    end = Offset(size.width, y),
                    strokeWidth = 1.5.dp.toPx(),
                    cap = StrokeCap.Round,
                    pathEffect = PathEffect.dashPathEffect(
                        floatArrayOf(1f, 6.dp.toPx()),
                        0f
                    )
                )
            }
    )
}
